import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface ApprovalRow {
  readonly approvalId: string;
  readonly reviewId: string;
  readonly approverUserId: string;
  readonly approvalLevelCode: string;
  readonly decisionCode: string;
  readonly decisionRemarks: string;
  readonly approvedCostInr: number | null;
  readonly delegatedFromUserId: string | null;
  readonly delegatedFromName: string | null;
  readonly delegatedFromCode: string | null;
  readonly delegationId: string | null;
  readonly snapshotData: Record<string, unknown> | null;
  readonly createdAt: string;
}


const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};

const APPROVAL_LEVELS = ['SALES_HEAD', 'REGIONAL_MANAGER', 'EXECUTIVE', 'DELEGATE'] as const;

export function ShApprovalHistoryPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Record approval form
  const [showForm, setShowForm] = useState(false);
  const [approvalLevel, setApprovalLevel] = useState<string>('SALES_HEAD');
  const [decision, setDecision] = useState('APPROVE');
  const [remarks, setRemarks] = useState('');
  const [approvedCost, setApprovedCost] = useState('');

  const load = useCallback(async () => {
    if (!reviewId) return;
    try {
      const r = await apiRequest<ApprovalRow[]>(`/v1/sh/review/${encodeURIComponent(reviewId)}/approvals`, { token });
      setRows(r); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const handleRecord = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (remarks.length < 10) { setError(t('sh.common.remarksMin10')); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      const body: Record<string, unknown> = {
        reviewId, decisionCode: decision,
        decisionRemarks: remarks, approvalLevelCode: approvalLevel,
      };
      if (approvedCost) body.approvedCostInr = Number(approvedCost);
      await apiRequest(`/v1/sh/review/${encodeURIComponent(reviewId!)}/approval`, {
        method: 'POST', token, body,
      });
      setInfo(t('sh.history.approvalRecorded'));
      setShowForm(false); setRemarks(''); setApprovedCost('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, approvalLevel, decision, remarks, approvedCost, token, busy, load]);

  return (
    <main aria-label="sh-approval-history">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to={`/sh/review/${reviewId}`} data-testid="back-review">{t('sh.common.review')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.history.title')}</h1>
        <Button type="button" variant="secondary" onClick={() => setShowForm(!showForm)} data-testid="btn-record-approval"
                style={{ marginLeft: 'auto' }}>{t('sh.history.recordApproval')}</Button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {showForm && (
        <section style={CARD} aria-label="record-approval-form">
          <form onSubmit={handleRecord} aria-label="approval-form">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginBottom: 8 }}>
              <div>
                <label htmlFor="ap-level" style={{ fontSize: 11 }}>{t('sh.history.approvalLevel')}</label>
                <select id="ap-level" value={approvalLevel} onChange={(e) => setApprovalLevel(e.target.value)}
                        data-testid="select-ap-level"
                        style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  {APPROVAL_LEVELS.map((l) => <option key={l} value={l}>{l.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="ap-decision" style={{ fontSize: 11 }}>{t('sh.common.decision')}</label>
                <select id="ap-decision" value={decision} onChange={(e) => setDecision(e.target.value)}
                        data-testid="select-ap-decision"
                        style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                  <option value="APPROVE">{t('sh.history.decApprove')}</option><option value="REJECT">{t('sh.history.decReject')}</option>
                  <option value="CONDITIONAL">{t('sh.history.decConditional')}</option>
                </select>
              </div>
              <div>
                <label htmlFor="ap-cost" style={{ fontSize: 11 }}>{t('sh.history.approvedCost')}</label>
                <input id="ap-cost" type="number" value={approvedCost} onChange={(e) => setApprovedCost(e.target.value)}
                       data-testid="input-ap-cost"
                       style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label htmlFor="ap-remarks" style={{ fontSize: 11 }}>{t('sh.history.remarksLabel')}</label>
              <textarea id="ap-remarks" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        data-testid="input-ap-remarks"
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-submit-approval">{t('sh.history.record')}</Button>
              <Button type="button" variant="danger" onClick={() => setShowForm(false)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.history.empty')}</p>
      ) : (
        <DataTable<ApprovalRow>
          ariaLabel="approval-table"
          rows={rows}
          rowKey={(a) => a.approvalId}
          columns={[
            { key: 'level', header: t('sh.common.level'), render: (a) => a.approvalLevelCode },
            {
              key: 'decision', header: t('sh.common.decision'),
              render: (a) => (
                <span style={{ color: a.decisionCode === 'APPROVE' ? 'var(--color-success)' : a.decisionCode === 'REJECT' ? 'var(--color-danger)' : 'inherit' }}>
                  {a.decisionCode}
                </span>
              ),
            },
            { key: 'cost', header: t('sh.common.cost'), render: (a) => <>₹{money(a.approvedCostInr)}</> },
            {
              key: 'remarks', header: t('sh.common.remarks'),
              render: (a) => (
                <span style={{ display: 'inline-block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.decisionRemarks}
                </span>
              ),
            },
            { key: 'delegated', header: t('sh.history.delegated'), render: (a) => (a.delegatedFromUserId ? t('sh.history.delegatedFrom', { user: personLabel(a.delegatedFromName, a.delegatedFromCode) ?? '—' }) : '—') },
            { key: 'date', header: t('sh.common.date'), render: (a) => <span style={{ fontSize: 12 }}>{at(a.createdAt)}</span> },
          ]}
        />
      )}
    </main>
  );
}
